import { ELEVEN_LABS_LANGUAGES } from '@/components/Voice/voiceInfo';
import { useTiming } from '@/hooks/useTiming';
import { getAllVoices, getAllVoicesAnonymous } from '@/services/api';
import { useAuth } from '@/services/auth';
import { ALL_VOICES_ID, languageToVoicesMap } from '@/state/presenters';
import { groupBy, isNonPremiumUser } from '@/utils/utils';
import { IVoice, Providers } from '@d-id/types';
import { useQuery } from 'react-query';
import { useSetRecoilState } from 'recoil';

// By providing staleTime to the query, we can avoid re-fetching the voice list on every reference to useAllVoices hook.
// It is highly probable that we can even set the staleTime to higher values or even Infinity, but we need to test this.
// Anyway, as long as we invalidate or update the cache when the user makes changes via the UI, we should be fine.
const ALL_VOICES_CACHE_STALE_TIME = 1000 * 30; // 30 seconds in case voice list changes externally

export function useAllVoices(condition: boolean = true, providersWhiteList?: Providers[]) {
    const { isAuthenticated, user } = useAuth();
    const { wrapWithTiming } = useTiming();
    const setLanguageToVoicesMap = useSetRecoilState(languageToVoicesMap);
    const apiQuery = isAuthenticated
        ? wrapWithTiming('getAllVoices', getAllVoices)
        : wrapWithTiming('getAllVoicesAnonymous', getAllVoicesAnonymous);
    const isProPlus = isNonPremiumUser(user?.planGroup);

    return useQuery(ALL_VOICES_ID, apiQuery, {
        select: voices => {
            return providersWhiteList ? voices.filter(voice => providersWhiteList.includes(voice.provider)) : voices;
        },
        refetchOnWindowFocus: false,
        placeholderData: [],
        onSuccess: (voices = []) => {
            const { premium = [], publicVoices = [] } = groupBy(voices, item => (item.access !== 'public' ? 'premium' : 'publicVoices'));

            const voicesToDisplay = isProPlus ? [...publicVoices, ...premium] : [...premium, ...publicVoices];
            const compareVoices = (a: IVoice, b: IVoice): number => {
                if (a.provider < b.provider) {
                    return -1;
                } else if (a.provider > b.provider) {
                    return 1;
                } else {
                    return a.name > b.name ? -1 : 1;
                }
            };

            const langToVoices = voicesToDisplay
                .filter(voice => voice.provider !== Providers.Amazon)
                .reduce(
                    (acc, voice) => {
                        const { language, locale, id } = voice;

                        if (language && locale && id) {
                            if (!acc[language]) {
                                acc[language] = [];
                            }
                            acc[language].push(voice);
                        }

                        return acc;
                    },
                    {} as Record<string, IVoice[]>
                );

            const elevenlabsVoices = voicesToDisplay.filter(voice => voice.provider === Providers.Elevenlabs);
            const elevenlabsLangToVoices = ELEVEN_LABS_LANGUAGES.reduce(
                (acc, language) => {
                    return {
                        ...acc,
                        [language]: elevenlabsVoices.filter(voice => voice.language.toLowerCase().includes(language.toLowerCase())),
                    };
                },
                {} as Record<string, IVoice[]>
            );

            const mergedLangToVoices = { ...langToVoices, ...elevenlabsLangToVoices };

            Object.entries(mergedLangToVoices).forEach(([, voices]) => {
                voices.sort(compareVoices);
            });

            setLanguageToVoicesMap(mergedLangToVoices);
        },
        enabled: condition,
        staleTime: ALL_VOICES_CACHE_STALE_TIME,
    });
}
